import { Card, LinkButton } from '@pmi/dsm-react';
import { EditIcon } from '@pmi/dsm-react/dist/components/icons/fa/16';
import { useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import usePermissions from '../../../shared/hooks/usePermissions';
import { useGetAccountDataQuery } from '../../../shared/navigation/apiSlice';
import PERMISSIONS from '../../../shared/permissions';
import { accountSelector } from '../../../shared/store/shared/accountSlice';
import { useSaveWelcomeContentMutation } from './apiSlice';
import EditWelcomeMessageForm from './EditWelcomeMessageForm';
import { EditWelcomeMessageFormModel } from './models';
import ViewWelcomeMessageForm from './ViewWelcomeMessageForm';
import styles from './welcomeMessageCard.module.scss';
import { toastWithAnalytics } from '../../../shared/analytics';

export default function WelcomeMessageCard() {
  const [editMode, setEditMode] = useState(false);
  const { currentAccountId } = useSelector(accountSelector);
  const { hasAccess } = usePermissions();
  const hasSaveTrainingHubWelcomeContentAccess = hasAccess({
    permissions: [PERMISSIONS.SaveTrainingHubWelcomeContent],
  });
  const {
    data: accountData,
    isLoading,
    isFetching,
  } = useGetAccountDataQuery(currentAccountId);
  const [saveWelcomeContent] = useSaveWelcomeContentMutation();

  const onSaveWelcomeContent = async (model: EditWelcomeMessageFormModel) => {
    try {
      await saveWelcomeContent({
        accountId: currentAccountId,
        payload: {
          content: model.content,
          title: model.title,
          transcriptData: model.transcript,
          videoUrl: model.videoUrl,
        },
      }).unwrap();
      toastWithAnalytics('Welcome content saved successfully', 'success');
    } catch (ex) {
      toastWithAnalytics('Error while saving welcome content', 'danger');
    }
    setEditMode(false);
  };

  return (
    <Card className="p-0">
      <div className="p-4">
        <div className="d-flex flex-column">
          <div className="d-flex flex-row">
            <div className="flex-fill">
              <h4 className="fw-bold mb-0">Welcome Message</h4>
              <p className="mb-0">
                Provide a custom message, instructions, or other info here. All
                users of your Partner Platform can see this content.
              </p>
            </div>
            {hasSaveTrainingHubWelcomeContentAccess && (
              <div>
                {!editMode && (
                  <LinkButton
                    titleText="Edit"
                    size="sm"
                    variant="light"
                    icon={EditIcon}
                    className={styles.actionBtn}
                    onClick={() => setEditMode(!editMode)}
                  />
                )}
                {editMode && (
                  <LinkButton
                    titleText="Cancel"
                    size="sm"
                    variant="light"
                    className={styles.actionBtn}
                    onClick={() => setEditMode(!editMode)}
                  />
                )}
              </div>
            )}
          </div>
          <hr />
          {(isLoading || isFetching) && (
            <div className="w-100 h-100 d-flex justify-content-center align-items-center">
              <Spinner />
            </div>
          )}
          {!(isLoading || isFetching) && !editMode && (
            <ViewWelcomeMessageForm
              email={accountData?.accountContactEmail}
              model={accountData.welcome}
              transcript={accountData?.videoTranscriptData}
              logoUrl={accountData.logoUrl}
            />
          )}
          {!(isLoading || isFetching) && editMode && (
            <EditWelcomeMessageForm
              model={accountData}
              saveWelcomeContent={onSaveWelcomeContent}
            />
          )}
        </div>
      </div>
    </Card>
  );
}
